import React, { useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Space, Tag, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import EventConfigurationContext from '@totem/components/services/threats/configuration/EventConfigurationContext';
import {
  EventConfigurationItem,
  STATUS_OPTIONS,
} from '@totem/components/services/threats/configuration/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { FilterField } from '@totem/components/ui_components/reportSettings/types';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';

const impactColor = (level: string) => {
  if (level === 'High') return 'red';
  if (level === 'Medium') return 'orange';
  return 'default';
};

const uniqueSortedValues = (values: (string | undefined)[]): string[] =>
  Array.from(new Set(values.filter((value): value is string => !!value))).sort(
    (left, right) => left.localeCompare(right),
  );

const selectedValues = (filters: FilterField[], key: string): string[] =>
  filters
    .find((filter) => filter.key === key)
    ?.values?.map((val) => val.value) ?? [];

const compareItems = (
  left: EventConfigurationItem,
  right: EventConfigurationItem,
  field: string,
): number => {
  switch (field) {
    case 'category':
      return left.category.localeCompare(right.category);
    case 'status':
      return (left.status || 'Active').localeCompare(right.status || 'Active');
    case 'sequence':
      return (left.sequence ?? 100) - (right.sequence ?? 100);
    default:
      return 0;
  }
};

const EventConfigurationTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const navigate = useNavigate();
  const { loading, categories, items, onEditItem, onDeleteItem } = useContext(
    EventConfigurationContext,
  );
  // Sort/filter/pagination state and persistence come from InputContainer (see the
  // parent EventConfigurationLayout) -- applied here to the already-fetched local
  // `items` array rather than a server query, since this report's full dataset is
  // small and fetched once. setFilterOptions is a context setter, not stable across
  // renders, so it's deliberately left out of these effects' dependency arrays
  // (react-hooks/exhaustive-deps is off project-wide) -- including it would refire on
  // every InputContainer render and loop.
  const { input, handleTableChange, setFilterOptions } =
    useContext(InputContext);

  const canEdit =
    permissions.internalSystemAdministrator.hasPermissions(userProfile);

  useEffect(() => {
    setFilterOptions(
      'category',
      categories.map((category) => ({
        text: `${category.name} (${category.id})`,
        value: category.id,
      })),
    );
  }, [categories]);

  useEffect(() => {
    setFilterOptions(
      'status',
      STATUS_OPTIONS.map((status) => ({ text: status, value: status })),
    );
  }, []);

  useEffect(() => {
    setFilterOptions(
      'organization',
      uniqueSortedValues(
        items.map((item) => item.hierarchy?.organization?.name),
      ).map((value) => ({ text: value, value })),
    );
  }, [items]);

  useEffect(() => {
    setFilterOptions(
      'ticketCategory',
      uniqueSortedValues(
        items.map((item) => item.ticketConfiguration.category),
      ).map((value) => ({ text: value, value })),
    );
  }, [items]);

  useEffect(() => {
    setFilterOptions(
      'ticketSubCategory',
      uniqueSortedValues(
        items.map((item) => item.ticketConfiguration.subCategory),
      ).map((value) => ({ text: value, value })),
    );
  }, [items]);

  useEffect(() => {
    setFilterOptions('create', [
      { text: 'Yes', value: 'true' },
      { text: 'No', value: 'false' },
    ]);
  }, []);

  const visibleItems = useMemo(() => {
    const categoryValues = selectedValues(input.filters, 'category');
    const statusValues = selectedValues(input.filters, 'status');
    const organizationValues = selectedValues(input.filters, 'organization');
    const ticketCategoryValues = selectedValues(
      input.filters,
      'ticketCategory',
    );
    const ticketSubCategoryValues = selectedValues(
      input.filters,
      'ticketSubCategory',
    );
    const createValues = selectedValues(input.filters, 'create');

    let filtered = items;
    if (categoryValues.length > 0) {
      filtered = filtered.filter((item) =>
        categoryValues.includes(item.category),
      );
    }
    if (statusValues.length > 0) {
      filtered = filtered.filter((item) =>
        statusValues.includes(item.status || 'Active'),
      );
    }
    if (organizationValues.length > 0) {
      filtered = filtered.filter((item) =>
        organizationValues.includes(item.hierarchy?.organization?.name ?? ''),
      );
    }
    if (ticketCategoryValues.length > 0) {
      filtered = filtered.filter((item) =>
        ticketCategoryValues.includes(item.ticketConfiguration.category),
      );
    }
    if (ticketSubCategoryValues.length > 0) {
      filtered = filtered.filter((item) =>
        ticketSubCategoryValues.includes(item.ticketConfiguration.subCategory),
      );
    }
    if (createValues.length > 0) {
      filtered = filtered.filter((item) =>
        createValues.includes(String(item.ticketConfiguration.create)),
      );
    }

    if (!input.sort.field) return filtered;

    const direction = input.sort.direction === 'descend' ? -1 : 1;
    return [...filtered].sort(
      (left, right) => compareItems(left, right, input.sort.field) * direction,
    );
  }, [items, input.filters, input.sort]);

  const sortOrderFor = (key: string): 'ascend' | 'descend' | null =>
    input.sort.field === key
      ? (input.sort.direction as 'ascend' | 'descend')
      : null;

  const columns: ColumnProps<EventConfigurationItem>[] = [
    {
      title: 'Category',
      key: 'category',
      width: 120,
      sorter: true,
      sortOrder: sortOrderFor('category'),
      render: (_, record) => <code>{record.category}</code>,
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      sorter: true,
      sortOrder: sortOrderFor('status'),
      render: (_, record) => (
        <Tag color={record.status === 'Active' ? 'green' : 'default'}>
          {record.status || 'Active'}
        </Tag>
      ),
    },
    {
      title: 'Seq',
      key: 'sequence',
      width: 100,
      sorter: true,
      sortOrder: sortOrderFor('sequence'),
      render: (_, record) => <span>{record.sequence ?? 100}</span>,
    },
    {
      title: 'Hierarchy',
      key: 'hierarchy',
      render: (_, record) => {
        const names = [
          record.hierarchy?.organization?.name,
          record.hierarchy?.region?.name,
          record.hierarchy?.building?.name,
          record.hierarchy?.controlSystem?.name,
        ].filter((name): name is string => !!name);

        return names.length > 0 ? (
          <span>{names.join(' / ')}</span>
        ) : (
          <Tag>Global</Tag>
        );
      },
    },
    {
      title: 'Example',
      key: 'example',
      ellipsis: true,
      render: (_, record) => <span>{record.example}</span>,
    },
    {
      title: 'Create Ticket',
      key: 'create',
      width: 110,
      render: (_, record) =>
        record.ticketConfiguration.create ? (
          <Tag color="green">Yes</Tag>
        ) : (
          <Tag>No</Tag>
        ),
    },
    {
      title: 'Impact',
      key: 'impact',
      width: 90,
      render: (_, record) => (
        <Tag color={impactColor(record.ticketConfiguration.impact)}>
          {record.ticketConfiguration.impact}
        </Tag>
      ),
    },
    {
      title: 'Urgency',
      key: 'urgency',
      width: 90,
      render: (_, record) => (
        <Tag color={impactColor(record.ticketConfiguration.urgency)}>
          {record.ticketConfiguration.urgency}
        </Tag>
      ),
    },
    {
      title: 'Ticket Category',
      key: 'ticketCategory',
      render: (_, record) => <span>{record.ticketConfiguration.category}</span>,
    },
    {
      title: 'Ticket Sub-Category',
      key: 'ticketSubCategory',
      render: (_, record) => (
        <span>{record.ticketConfiguration.subCategory}</span>
      ),
    },
    {
      title: 'Event Matches',
      key: 'eventMatches',
      width: 120,
      render: (_, record) => <Tag>{(record.eventMatches ?? []).length}</Tag>,
    },
    {
      title: '',
      key: 'actions',
      width: canEdit ? 130 : 50,
      render: (_, record) => (
        <Space>
          {record.ticketConfiguration.create && (
            <Tooltip title="View Rule">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/dashboard/admin/rules/${record.id}`)}
              />
            </Tooltip>
          )}
          {canEdit && (
            <>
              <Tooltip title="Edit">
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => onEditItem(record)}
                />
              </Tooltip>
              <Popconfirm
                title="Delete this configuration item?"
                description="This cannot be undone."
                onConfirm={() => onDeleteItem(record.id)}
                okText="Delete"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="Delete">
                  <Button size="small" icon={<DeleteOutlined />} danger />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <IBTable
      rowKey={(record) => record.id}
      columns={columns}
      dataSource={visibleItems}
      loading={loading}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: visibleItems.length,
        showSizeChanger: true,
      }}
      onChange={handleTableChange}
    />
  );
};

export default EventConfigurationTable;
